import * as Immutable from "immutable";
import flatten from "lodash/flatten";
import defaultTitle from "views/components/defaultTitle";
import generateObjectId from "logic/generateObjectId";
import ViewState from "./ViewState";
const _View = class _View {
  constructor(id, type, title, summary, description, search, properties, state, createdAt, owner, requires, favorite, lastUpdatedAt, entitySource) {
    this._value = {
      id,
      type,
      title,
      summary,
      description,
      search,
      properties: Immutable.fromJS(properties),
      state: Immutable.fromJS(state),
      createdAt,
      owner,
      requires,
      favorite,
      lastUpdatedAt,
      entitySource
    };
  }
  static create() {
    return new Builder().createdAt(/* @__PURE__ */ new Date()).build();
  }
  get id() {
    return this._value.id;
  }
  get type() {
    return this._value.type;
  }
  get title() {
    return this._value.title;
  }
  get summary() {
    return this._value.summary;
  }
  get description() {
    return this._value.description;
  }
  get search() {
    return this._value.search;
  }
  get properties() {
    return this._value.properties;
  }
  get state() {
    return this._value.state;
  }
  get createdAt() {
    return this._value.createdAt;
  }
  get widgetMapping() {
    return (this.state || Immutable.Map()).valueSeq().map((s) => s.widgetMapping).reduce((prev, cur) => Immutable.fromJS(prev).merge(Immutable.fromJS(cur)));
  }
  get owner() {
    return this._value.owner;
  }
  get requires() {
    return this._value.requires || {};
  }
  get favorite() {
    return this._value.favorite || false;
  }
  get lastUpdatedAt() {
    return this._value.lastUpdatedAt;
  }
  get entitySource() {
    return this._value.entitySource;
  }
  getSearchTypeByWidgetId(widgetId) {
    const widgetMapping = this.state.map((state) => state.widgetMapping).flatten(true);
    const searchTypeId = widgetMapping.get(widgetId).first();
    if (!searchTypeId) {
      throw new Error(`Search type for widget with id ${widgetId} does not exist`);
    }
    const searchTypes = flatten(this.search.queries.map((query) => query.searchTypes).toArray());
    return searchTypes.find((entry) => entry && entry.id && entry.id === searchTypeId);
  }
  getWidgetTitleByWidget(widget) {
    const widgetTitles = this.state.flatMap((state) => state.titles.get("widget"));
    const _defaultTitle = defaultTitle(widget);
    return widgetTitles.get(widget.id) || _defaultTitle;
  }
  toBuilder() {
    const {
      id,
      title,
      summary,
      description,
      search,
      properties,
      state,
      createdAt,
      owner,
      requires,
      type,
      favorite,
      lastUpdatedAt,
      entitySource
    } = this._value;
    return new Builder(
      Immutable.Map({
        id,
        title,
        summary,
        description,
        search,
        properties,
        state,
        createdAt,
        owner,
        requires,
        type,
        favorite,
        lastUpdatedAt,
        entitySource
      })
    );
  }
  toJSON() {
    const { id, type, title, summary, description, search, properties, state, createdAt, owner } = this._value;
    return {
      id,
      type,
      title,
      summary,
      description,
      search_id: search.id,
      properties,
      state,
      created_at: createdAt,
      owner
    };
  }
  static fromJSON(value) {
    const {
      id,
      type,
      title,
      summary,
      description,
      properties,
      state,
      created_at,
      owner,
      requires,
      favorite,
      last_updated_at,
      _entity_source
    } = value;
    const viewState = Immutable.Map(state).map(ViewState.fromJSON).toMap();
    const createdAtDate = new Date(created_at);
    const lastUpdatedAtDate = last_updated_at ? new Date(last_updated_at) : void 0;
    return _View.create().toBuilder().id(id).type(type).title(title).summary(summary).description(description).properties(properties).state(viewState).createdAt(createdAtDate).owner(owner).requires(requires).favorite(favorite).lastUpdatedAt(lastUpdatedAtDate).entitySource(_entity_source).build();
  }
  static builder() {
    return new Builder();
  }
};
_View.Type = {
  Search: "SEARCH",
  Dashboard: "DASHBOARD"
};
let View = _View;
export {
  View as default
};
class Builder {
  constructor(value = Immutable.Map()) {
    this.value = value;
  }
  id(value) {
    return new Builder(this.value.set("id", value));
  }
  toNewView() {
    return new Builder(this.value.set("id", void 0).set("title", void 0));
  }
  type(value) {
    return new Builder(this.value.set("type", value));
  }
  newId() {
    return this.id(generateObjectId());
  }
  title(value) {
    return new Builder(this.value.set("title", value));
  }
  summary(value) {
    return new Builder(this.value.set("summary", value));
  }
  description(value) {
    return new Builder(this.value.set("description", value));
  }
  search(value) {
    return new Builder(this.value.set("search", value));
  }
  properties(value) {
    return new Builder(this.value.set("properties", value));
  }
  state(value) {
    return new Builder(this.value.set("state", Immutable.Map(value)));
  }
  createdAt(value) {
    return new Builder(this.value.set("createdAt", value));
  }
  lastUpdatedAt(value) {
    return new Builder(this.value.set("lastUpdatedAt", value));
  }
  entitySource(value) {
    return new Builder(this.value.set("entitySource", value));
  }
  owner(value) {
    return new Builder(this.value.set("owner", value));
  }
  requires(value) {
    return new Builder(this.value.set("requires", value));
  }
  favorite(value) {
    return new Builder(this.value.set("favorite", value));
  }
  build() {
    const {
      id,
      type,
      title,
      summary,
      description,
      search,
      properties,
      state,
      createdAt,
      owner,
      requires,
      favorite,
      lastUpdatedAt,
      entitySource
    } = this.value.toObject();
    return new View(
      id,
      type,
      title,
      summary,
      description,
      search,
      properties,
      state,
      createdAt,
      owner,
      requires,
      favorite,
      lastUpdatedAt,
      entitySource
    );
  }
}
